import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DELETE_DRAFT, ReportStatus, UpdateReport } from 'src/api';
import axios from 'src/axios';
import { Button } from 'src/components/atoms';
import { HeaderText, InfoText } from 'src/components/atomsLegacy';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  handleSuccessToast,
  handleWarningToast,
} from 'src/components/molecules';
import { reportDetailsVm } from 'src/components/pages/Case/Case.slice';
import { resetCurrentReport } from 'src/creators/MCC.creators';
import {
  useDeleteDraftReportMutation,
  UserType,
  useSubmitDraftReportMutation,
} from 'src/graphql';
import { useWindowDimensions } from 'src/hooks';
import { AppDispatch } from 'src/store';
import theme from 'src/theme/theme';
import * as Types from '../../../types';
import { AddUserModal } from '../AddUserModal/AddUserModal';
import { ConfirmDialog } from '../ConfirmDialog';
import {
  ButtonContainer,
  Container,
  InfoContainer,
  StyledLink,
} from './CreateReportHeader.styles';
import { checkDraftReportIsValid } from './checkDraftReportIsValid';
import { submitAndAssignRandomCM } from './submitAndAssignRandomCM';
import { useCaseStatus } from './useCaseStatus';

interface Props {
  reportID: string;
  currentReport: UpdateReport | null;
  isExistingReport: boolean;
  currentUser?: Types.User;
  updateReport: (reportID: string, report: UpdateReport) => Promise<void>;
  areDraftReportFlagsEnabled?: boolean;
}

const canViewerAccessCase = (
  currentReport: UpdateReport | null,
  redirect: boolean,
  isDeleted: boolean,
  canAdministerReportPermissions: boolean,
  caseID?: string,
  currentUser?: Types.User,
) => {
  const isAssignee = currentReport?.assignee === currentUser?.id;
  const isCaseLead = currentReport?.cases?.[0].caseLead?.length
    ? Number.parseInt(currentReport?.cases?.[0].caseLead, 10) ===
      currentUser?.id
    : false;
  return Boolean(
    redirect &&
    (canAdministerReportPermissions || isAssignee || isCaseLead) &&
    caseID &&
    !isDeleted,
  );
};

export const CreateReportHeader = ({
  reportID,
  currentReport,
  isExistingReport,
  currentUser,
  updateReport,
  areDraftReportFlagsEnabled = false,
}: Props) => {
  const [isDeleted, setDeleted] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [deleteReportDialog, setDeleteReportDialog] = useState(false);
  const [deletingInProgress, setDeletingInProgress] = useState(false);
  const [showCMSelect, setShowCMSelect] = useState(false);
  const { width } = useWindowDimensions();

  const { mutate: deleteDraftReportMutation } = useDeleteDraftReportMutation({
    onSuccess: () => {
      setDeleted(true);
      setRedirect(true);
      setDeleteReportDialog(false);
      handleSuccessToast({ toastMessage: 'Draft report successfully deleted' });
    },
    onMutate: () => {
      setDeletingInProgress(true);
    },
    onError: () => {
      setDeletingInProgress(false);
      handleWarningToast({
        toastMessage: 'Failed to delete draft report',
      });
    },
  });

  const { mutate: deleteDraftReport } = useMutation({
    mutationFn: () => axios.delete(DELETE_DRAFT(reportID)),
    onSuccess: () => {
      setDeleted(true);
      setRedirect(true);
      setDeleteReportDialog(false);
      handleSuccessToast({ toastMessage: 'Draft report successfully deleted' });
    },
    onMutate: () => {
      setDeletingInProgress(true);
    },
  });

  const { mutate: submitDraftReportMutation } = useSubmitDraftReportMutation({
    onSuccess: () => {
      setRedirect(true);
      handleSuccessToast({
        toastMessage: 'Case manager assigned and report submitted',
      });
    },
    onError: () => {
      handleWarningToast({
        toastMessage: 'Failed to submit draft report',
      });
    },
  });

  const deleteReport = () => {
    if (areDraftReportFlagsEnabled) {
      deleteDraftReportMutation({ reportId: reportID });
    } else {
      deleteDraftReport();
    }
  };

  const {
    permissions: { canAdministerReportPermissions },
  } = useSelector(reportDetailsVm({ reportId: reportID }));

  const dispatch = useDispatch<AppDispatch>();
  const caseID = useSelector(
    ({ manualCaseCreation }: Types.State) =>
      manualCaseCreation.mccReducer.currentReport?.cases?.[0].id,
  );
  const { status, refetchAttributes } = useCaseStatus(caseID);

  const toggleCMSelect = (shouldShow: boolean) => {
    setShowCMSelect(shouldShow);
    refetchAttributes();
  };

  const submit = () => {
    if (currentReport && checkDraftReportIsValid(currentReport)) {
      toggleCMSelect(true);
    }
  };

  const hasMIPermissions = useSelector(
    (state: Types.State) =>
      (state?.globalUsers?.currentUser?.systemPermissions?.includes(
        'CREATE_NEW_REPORT',
      ) ||
        state?.globalUsers?.currentUser?.systemPermissions?.includes(
          'CREATE_EXISTING_REPORT',
        )) ??
      false,
  );

  useEffect(() => {
    if (currentReport?.status && currentReport?.status !== ReportStatus.DRAFT) {
      dispatch(resetCurrentReport());
      setRedirect(true);
      handleWarningToast({
        toastMessage: 'This report has already been submitted',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportID, currentReport]);

  const updateCMAndSubmit = ({
    internalUser,
    selectedUser,
  }: {
    internalUser: boolean;
    selectedUser: UserType;
    email: string;
    name: string;
  }) => {
    if (internalUser && currentReport) {
      toggleCMSelect(false);

      if (areDraftReportFlagsEnabled) {
        const variables = {
          reportId: reportID,
          submissionMode: Types.SUBMISSION_MODE.MCC,
          caseLead: currentReport?.cases?.[0]?.caseLead
            ? Number.parseFloat(currentReport?.cases?.[0]?.caseLead)
            : undefined,
        };

        if (selectedUser?.id) {
          return updateReport(reportID, {
            ...currentReport,
            assignee: selectedUser.id,
          }).then(() => submitDraftReportMutation(variables));
        }

        submitDraftReportMutation(variables);
        return;
      }

      const shouldAssignRandomCM = hasMIPermissions && !selectedUser;

      if (shouldAssignRandomCM) {
        return submitAndAssignRandomCM(
          currentReport,
          setRedirect,
          shouldAssignRandomCM,
          status,
        );
      }
      return updateReport(reportID, {
        ...currentReport,
        assignee: selectedUser?.id,
      }).then(() => {
        return submitAndAssignRandomCM(
          currentReport,
          setRedirect,
          false,
          status,
        );
      });
    }
    toggleCMSelect(false);
  };

  const accessToCase = canViewerAccessCase(
    currentReport,
    redirect,
    isDeleted,
    Boolean(canAdministerReportPermissions),
    caseID,
    currentUser,
  );

  return (
    <Container tablet={width < 1000}>
      {accessToCase && <Redirect to={`/case?id=${caseID}`} />}
      {redirect && (!accessToCase || isDeleted) && <Redirect to={'/cases'} />}
      <InfoContainer>
        <StyledLink to="/cases" data-testid="report-back-button">
          <img src="/assets/manualIntake/backToCases.svg" alt="Back to cases" />
          Back to cases
        </StyledLink>
        <HeaderText
          label={isExistingReport ? 'Add Existing Report' : 'Submit Report'}
        />
        <InfoText
          label={
            isExistingReport
              ? 'Add an existing report into Vault Platform. Reports can be at any stage including closed.'
              : 'Liaise with reporter and enter the following details to manually submit on their behalf.'
          }
          color={theme.newPalette.neutral.N1000}
        />
      </InfoContainer>
      <ButtonContainer mobile={width < 500}>
        <Button
          data-testid="delete-report"
          onClick={() => setDeleteReportDialog(true)}
          variant="secondary"
          size="medium"
          width="120px"
        >
          <StyledIconInButton
            size="medium"
            position="left"
            className="uil uil-trash-alt"
          />
          Delete
        </Button>
        <Button
          data-testid="submit-report"
          onClick={submit}
          variant="primary"
          width="120px"
          size="medium"
        >
          {isExistingReport ? 'Add Report' : 'Submit'}
        </Button>
      </ButtonContainer>
      <ConfirmDialog
        open={deleteReportDialog}
        setOpen={setDeleteReportDialog}
        title="Delete Report?"
        body="Are you sure you want to delete this report? You will not be able recover the report after deletion."
        onCancel={() => setDeleteReportDialog(false)}
        onConfirm={deleteReport}
        data-testid="delete-report-dialog"
        isConfirmButtonDisabled={deletingInProgress}
        loading={deletingInProgress}
      />
      {showCMSelect && (
        <AddUserModal
          showOnlyCM={true}
          open={showCMSelect}
          setOpen={setShowCMSelect}
          title="Case Manager"
          currentReport={currentReport}
          reportID={currentReport?.report ?? ''}
          data-testid="case-manager"
          buttonText="Confirm"
          addUserText="currently assigned as the case manager. You can update this by making a selection from the case managers below."
          alternateUserText="There is no case manager currently selected for this case. If you do not select a case manager one will be assigned automatically."
          subTitle=""
          updateReport={updateReport}
          updateData={updateCMAndSubmit}
          currentUser={currentUser}
          onlyAppUsers={false}
        />
      )}
    </Container>
  );
};
